import { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSessionInfo } from './useSessionInfo';

const MenuCatalogContext = createContext({
  menus: [],
  resolveMenuKey: () => null,
  currentMenuKey: null,
});

/**
 * Longest-prefix match of pathname against menu.path from backend.
 */
export function resolveMenuKeyFromPath(menus, pathname) {
  if (!pathname || !menus?.length) return null;
  let best = null;
  let bestLen = -1;
  for (const menu of menus) {
    if (!menu.path || menu.always_show) continue;
    const p = menu.path;
    if (pathname === p || pathname.startsWith(`${p}/`)) {
      if (p.length > bestLen) {
        best = menu.key;
        bestLen = p.length;
      }
    }
  }
  return best;
}

export function MenuCatalogProvider({ children }) {
  const { menus = [] } = useSessionInfo();
  const location = useLocation();

  const value = useMemo(() => {
    const resolveMenuKey = (pathname = location.pathname) =>
      resolveMenuKeyFromPath(menus, pathname);
    return {
      menus,
      resolveMenuKey,
      currentMenuKey: resolveMenuKey(location.pathname),
    };
  }, [menus, location.pathname]);

  return (
    <MenuCatalogContext.Provider value={value}>
      {children}
    </MenuCatalogContext.Provider>
  );
}

export function useMenuCatalog() {
  return useContext(MenuCatalogContext);
}

export function useCurrentMenuKey() {
  return useMenuCatalog().currentMenuKey;
}

export default MenuCatalogContext;
